import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SportsEsportsRoundedIcon from "@mui/icons-material/SportsEsportsRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LogoutIcon from "@mui/icons-material/Logout";
import { IconButton } from "@mui/material";
import type { AuthUser } from "../context/UserContext";
import { getApiBase } from "../utils/apiBase";
import {
  DISPLAY_NAME_PROFANITY_MESSAGE,
  displayNameContainsProfanity,
} from "../utils/displayNamePolicy";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Signed-in profile (optional for guest-only table name). */
  user: AuthUser | null;
  authToken: string | null;
  /** Current name shown at the table (guest or signed-in). */
  tableDisplayName: string;
  /** Apply name at the table (session + optional socket init). */
  onApplyTableName: (name: string) => void;
  /** After successful API update, refresh /api/me. */
  onAuthProfileUpdated?: (token: string) => void;
  /** When false, display name is read-only (e.g. during an active game). */
  tableNameEditable?: boolean;
  showChooseGame?: boolean;
  onChooseGame?: () => void;
  showLeaveGame?: boolean;
  onLeaveGame?: () => void;
  onLogout?: () => void;
};

export const ProfileModal = ({
  open,
  onClose,
  user,
  authToken,
  tableDisplayName,
  onApplyTableName,
  onAuthProfileUpdated,
  tableNameEditable = true,
  showChooseGame = false,
  onChooseGame,
  showLeaveGame = false,
  onLeaveGame,
  onLogout,
}: Props) => {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValue(tableDisplayName.trim() || user?.displayName?.trim() || "");
    setError(null);
  }, [open, tableDisplayName, user?.displayName]);

  const handleSave = async () => {
    if (!tableNameEditable) return;
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Enter a display name.");
      return;
    }
    if (displayNameContainsProfanity(trimmed)) {
      setError(DISPLAY_NAME_PROFANITY_MESSAGE);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (authToken) {
        const res = await fetch(`${getApiBase()}/api/me`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ displayName: trimmed }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || `Update failed (${res.status})`);
        }
        const data = (await res.json()) as { token: string };
        if (data.token) onAuthProfileUpdated?.(data.token);
      }
      onApplyTableName(trimmed);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const isSignedIn = Boolean(authToken && user);
  const profileName = (tableDisplayName.trim() || user?.displayName?.trim() || "Guest").trim();
  const profileSubLabel = isSignedIn ? (user?.email?.trim() || "Signed in account") : "Guest account";
  const nameProfane =
    tableNameEditable && value.trim().length > 0 && displayNameContainsProfanity(value);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="profile-dialog-title">
      <DialogTitle id="profile-dialog-title" sx={{ pb: 0.75, pt: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box component="span">Profile</Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)" }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important", pb: 2 }}>
        <Stack spacing={1.5} sx={{ pt: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
            {isSignedIn && user?.picture ? (
              <Avatar src={user.picture} alt="" sx={{ width: 48, height: 48 }} />
            ) : (
              <Avatar sx={{ width: 48, height: 48 }}>{profileName.charAt(0).toUpperCase()}</Avatar>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: "#fff" }}>{profileName}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap title={profileSubLabel}>
                {profileSubLabel}
              </Typography>
            </Box>
          </Box>

          <TextField
            autoFocus={tableNameEditable}
            label="Display name"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            disabled={!tableNameEditable}
            error={Boolean(error) || nameProfane}
            helperText={
              error ||
              (nameProfane
                ? DISPLAY_NAME_PROFANITY_MESSAGE
                : !tableNameEditable
                  ? "You can change this after the match ends or before the next one starts."
                  : `${value.trim().length}/48 characters`)
            }
            inputProps={{ maxLength: 48 }}
            fullWidth
            size="small"
          />
          <Button
            onClick={() => void handleSave()}
            variant="outlined"
            startIcon={<EditRoundedIcon />}
            disabled={saving || !tableNameEditable || nameProfane}
            sx={{ minHeight: 44, borderColor: "rgba(76,175,80,0.75)", color: "#fff" }}
          >
            {saving ? "Saving…" : "Change name"}
          </Button>
          {showChooseGame && onChooseGame ? (
            <Button
              onClick={() => {
                onClose();
                onChooseGame();
              }}
              variant="outlined"
              startIcon={<SportsEsportsRoundedIcon />}
              sx={{ minHeight: 44, borderColor: "rgba(76,175,80,0.75)", color: "#fff" }}
            >
              All games
            </Button>
          ) : null}
          {showLeaveGame && onLeaveGame ? (
            <Button
              onClick={() => {
                onClose();
                onLeaveGame();
              }}
              color="error"
              variant="outlined"
              startIcon={<LogoutIcon />}
              sx={{ minHeight: 44, borderColor: "rgba(244,67,54,0.72)", color: "#fff" }}
            >
              Leave game
            </Button>
          ) : null}
          {onLogout ? (
            <Button
              onClick={() => {
                onClose();
                onLogout();
              }}
              color="error"
              variant="contained"
              startIcon={<LogoutRoundedIcon />}
              sx={{ minHeight: 44, fontWeight: 700 }}
            >
              Logout
            </Button>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
